"use strict";

/**
 * DGF-3054 — the gating rules that decide whether an express wallet may be offered.
 *
 * Deliberately free of DOM, jQuery and cartridge-alias requires so it can be unit tested under
 * jest's node environment (jest.config.js has testEnvironment "node", no jsdom, and no alias for
 * app_adyen_SFRA).
 *
 * Three independent gates have to pass before a wallet is rendered:
 *
 *   1. Superdry's site preferences  — applied server-side, both in the ISML gate and in
 *      getCheckoutExpressPaymentMethods.js, which is what populates cartExpressMethods /
 *      pdpExpressMethods.
 *   2. Territory                    — the Adyen /paymentMethods response. Adyen is asked with the
 *      shopper's countryCode, currency and amount, and only returns wallets it will actually
 *      process. getWalletConfiguration() is that gate: no configuration, no button. Never render
 *      straight off a site preference.
 *   3. Device                       — each SDK component's own availability check.
 */

/**
 * The wallets WP3 implements renderers for. Amazon Pay and ADYEN PayPal express are out of scope
 * for DGF-3054, so no renderer exists for them and nothing here can mount one. Their site
 * preferences are switched off by configuration rather than by a code-level exclusion; this list
 * is a statement of what is implemented, not a filter aimed at a particular wallet.
 *
 * "paypal" here is DGF-4617's DIRECT PayPal express (int_paypal / int_paypal_overlay) — a local
 * wallet, not Adyen's paypal method. See LOCAL_WALLETS.
 */
const SUPPORTED_WALLETS = ["applepay", "googlepay", "paypal"];

/**
 * DGF-4617 — wallets provided by a local (non-Adyen) integration.
 *
 * A local wallet never appears in Adyen's /paymentMethods response, so the territory gate
 * (getWalletConfiguration) does not apply to it: its availability is decided by its own SDK and by
 * the server-side preference that controls whether the container lists it at all
 * (data-express-local-wallets, emitted only when the surface's sdPayPalExpress_* preference is on).
 */
const LOCAL_WALLETS = ["paypal"];

/**
 * Whether a wallet NAME belongs to a local integration.
 *
 * Use this only to validate what a container's data-express-local-wallets attribute may contain —
 * i.e. as a whitelist of implemented local wallets. To decide how to ROUTE a wallet, use
 * `isLocalWalletOnContainer` below, which asks the server rather than the name.
 *
 * @param {string} walletType - wallet name
 * @returns {boolean} whether the wallet is provided by a local (non-Adyen) integration
 */
function isLocalWallet(walletType) {
    return LOCAL_WALLETS.indexOf(walletType) > -1;
}

// The server's own statement of which local wallets THIS container offers.
const LOCAL_WALLETS_ATTRIBUTE = "data-express-local-wallets";

/**
 * Whether this container's wallet should be built by a LOCAL integration.
 *
 * REVIEW FINDING — routing used to key off the wallet NAME (`isLocalWallet`), and "paypal" means two
 * different things: it is in SUPPORTED_WALLETS as Adyen's method AND in LOCAL_WALLETS as DGF-4617's
 * direct integration. So if Adyen's own `PayPalExpress_Enabled` were ever switched on, Adyen's
 * `paypal` would arrive through the OFFERED list and be handed to the direct-PayPal component —
 * regardless of whether the server had listed it on the container at all. It failed closed only by
 * accident: with our preference off the container carries no data-express-place-order-url, so
 * createComponent returned null. Accidentally safe is not safe.
 *
 * Asking the container instead makes the routing match the server's intent exactly:
 *   - our preference on  → the attribute lists it → the direct component builds it, deliberately,
 *     and that stays true even if Adyen also starts offering paypal;
 *   - our preference off → no attribute → it falls through to the Adyen path, where an unrecognised
 *     type is rejected by the walletClasses lookup rather than silently misrouted.
 *
 * @param {Object} container - the express container element
 * @param {string} walletType - wallet name
 * @returns {boolean} whether to build this wallet through a local integration
 */
function isLocalWalletOnContainer(container, walletType) {
    if (!container || typeof container.getAttribute !== "function" || !walletType) {
        return false;
    }

    return (container.getAttribute(LOCAL_WALLETS_ATTRIBUTE) || "")
        .split(",")
        .map((wallet) => wallet.trim())
        .indexOf(walletType) > -1;
}

/**
 * ON, because Adyen documents the limitation:
 *
 *   "Google Pay express checkout is not supported on Android."
 *
 * (Matching DGF-707, 2026-04-21.) The SDK does not enforce it — Google Pay reports itself as
 * available on Android Chrome and will render a button that cannot complete the payment — so the
 * exclusion has to be applied here.
 *
 * Platform-based, not viewport-based. The design mock expressed the same intent by hiding Google
 * Pay below 768px, and its intent was right, but viewport width is not device capability and gets
 * it wrong in both directions: an Android tablet in landscape reads as desktop and would still be
 * offered the button the rule exists to remove, while a narrow desktop Chrome window reads as
 * mobile and would lose a wallet that works perfectly. Only the platform determines whether Adyen's
 * limitation applies.
 *
 * Note the limitation is Android-specific, not mobile-specific: on iOS the SDK's own availability
 * check decides, and Google Pay may legitimately be offered there.
 *
 * Removal is contingent on ADYEN lifting the documented limitation, not on our own testing. Do not
 * switch this off because Google Pay appears to work in one Android session; the constraint is
 * vendor-stated. Suppressions are recorded in window.sdExpressDiagnostics with reason
 * "platform-excluded", so an Android device showing no Google Pay button reads as deliberate rather
 * than as a silent failure.
 *
 * Adyen's other stated requirement — that Google Pay express is supported only with the Advanced
 * flow — is already satisfied: adyen/express/initializeCheckout.js constructs AdyenCheckout with no
 * `session` property, and the express path calls /paymentMethods then /payments. No change needed.
 */
const EXCLUDE_GOOGLE_PAY_ON_ANDROID = true;

const APPLE_PAY = "applepay";
const GOOGLE_PAY = "googlepay";
const PAY_WITH_GOOGLE = "paywithgoogle";

/**
 * Wallet type as named by Superdry/Adyen, mapped to the /paymentMethods types that satisfy it.
 * Google Pay is returned as either "googlepay" or "paywithgoogle" depending on merchant account
 * configuration.
 */
const WALLET_RESPONSE_TYPES = {
    applepay: [APPLE_PAY],
    googlepay: [GOOGLE_PAY, PAY_WITH_GOOGLE]
};

/**
 * Keeps only the wallets WP3 has a renderer for, preserving the server's ordering.
 *
 * Ordering is applied server-side from the ExpressPayments_order preference
 * (getCheckoutExpressPaymentMethods.js:17-25), so it must not be re-sorted here.
 *
 * @param {string[]} walletTypes - express method names from the payment methods response
 * @returns {string[]} the subset this cartridge can render
 */
function filterSupportedWallets(walletTypes) {
    if (!Array.isArray(walletTypes)) {
        return [];
    }
    return walletTypes.filter((type) => SUPPORTED_WALLETS.indexOf(type) > -1);
}

/**
 * Territory gate. Returns the per-wallet `configuration` object Adyen sent for this shopper's
 * country, currency and amount, or undefined when Adyen did not offer the wallet at all.
 *
 * For Google Pay this object carries merchantId and gatewayMerchantId from the Adyen Customer
 * Area. That is the authoritative source — window.googleMerchantID (used by the checkout drop-in)
 * must not be substituted here, it would override the Customer Area value.
 *
 * @param {Object} adyenPaymentMethods - the AdyenPaymentMethods node of the response
 * @param {string} walletType - "applepay" or "googlepay"
 * @returns {Object|undefined} the wallet configuration, or undefined when unavailable
 */
function getWalletConfiguration(adyenPaymentMethods, walletType) {
    const responseTypes = WALLET_RESPONSE_TYPES[walletType];
    const paymentMethods = adyenPaymentMethods && adyenPaymentMethods.paymentMethods;
    if (!responseTypes || !Array.isArray(paymentMethods)) {
        return undefined;
    }
    const match = paymentMethods.filter((pm) => pm && responseTypes.indexOf(pm.type) > -1)[0];
    return match ? match.configuration : undefined;
}

/**
 * Device gate. Asks the SDK component whether it can be used, mirroring
 * ApplePay.checkIfComponentIsAvailable (app_adyen_SFRA/.../applepay/applepay.js:299-309) so both
 * wallets are treated identically.
 *
 * Google Pay has no availability check anywhere in the vendor code — verified, zero matches for
 * isAvailable / isReadyToPay / canMakePayment in
 * adyen/express/paymentMethods/googlepay/googlepay.js — so without this it renders on devices that
 * cannot complete the payment. The SDK's own check is used rather than browser sniffing.
 *
 * A component that exposes no isAvailable() is treated as available, which is the vendor's
 * behaviour and avoids suppressing a wallet because of an SDK change.
 *
 * @param {Object} component - an Adyen SDK component
 * @returns {Promise<boolean>} whether the component reports itself usable
 */
async function isComponentAvailable(component) {
    if (!component) {
        return false;
    }
    if (typeof component.isAvailable !== "function") {
        return true;
    }
    try {
        const available = await component.isAvailable();
        return available !== false;
    } catch (error) {
        return false;
    }
}

/**
 * True when the user agent looks like Android.
 *
 * userAgentData.platform is preferred where the browser provides it (Chromium), because it is the
 * supported replacement for UA-string sniffing and is not subject to UA reduction; the UA string is
 * the fallback.
 *
 * @param {Object} [nav] - navigator-like object, injected for testing
 * @returns {boolean} whether the platform is Android
 */
function isAndroidPlatform(nav) {
    const navigatorRef = nav || (typeof navigator === "undefined" ? null : navigator);
    if (!navigatorRef) {
        return false;
    }
    const brands = navigatorRef.userAgentData && navigatorRef.userAgentData.platform;
    if (typeof brands === "string" && brands) {
        return brands.toLowerCase().indexOf("android") > -1;
    }
    return /android/i.test(navigatorRef.userAgent || "");
}

/**
 * Platform exclusions applied before a component is even created, so nothing is fetched or
 * mounted for a wallet we already know to suppress.
 *
 * @param {string} walletType - "applepay" or "googlepay"
 * @param {Object} [nav] - navigator-like object, injected for testing
 * @returns {boolean} whether the wallet is excluded on this platform
 */
function isWalletExcludedOnPlatform(walletType, nav) {
    if (walletType !== GOOGLE_PAY) {
        return false;
    }
    return EXCLUDE_GOOGLE_PAY_ON_ANDROID && isAndroidPlatform(nav);
}

/**
 * PDP eligibility: the wallet may only be offered for a concrete, orderable variant.
 *
 * Same signals as add-to-bag, but deliberately stricter. Superdry's own add-to-bag button gates on
 * `available` only (addToCartProduct.isml:5,8 and product/base.js:341-350 dropped readyToOrder,
 * which base SFRA checks) because the PDP preselects a variant. Express additionally requires
 * readyToOrder, because a master product cannot be express-bought — there is nothing to add to the
 * temporary basket.
 *
 * Strict equality on both, so a missing signal fails closed. The vendor's buildFallbackProduct
 * (product/expressPayments.js:30-31) defaults both to true when the DOM lookup misses, which on
 * Superdry's PDP always misses: the .global-availability element exists only in the update-cart
 * branch of addToCartActions.isml, never on a PDP.
 *
 * @param {Object} product - product payload from product:updateAddToCart, or server-rendered attrs
 * @returns {boolean} whether express may be offered for this product
 */
function isProductExpressEligible(product) {
    if (!product) {
        return false;
    }
    return product.readyToOrder === true && product.available === true;
}

/**
 * Reads a strict boolean data attribute. Anything other than the exact string "true" is false —
 * a missing or malformed attribute must not enable a payment button.
 *
 * @param {Object} element - element with a getAttribute method
 * @param {string} attributeName - attribute to read
 * @returns {boolean} the parsed value
 */
function readBooleanAttribute(element, attributeName) {
    if (!element || typeof element.getAttribute !== "function") {
        return false;
    }
    return element.getAttribute(attributeName) === "true";
}

/**
 * Converts a major-unit amount to minor units without hard-coding fraction digits.
 *
 * Amounts must never be multiplied by a literal 100. The retired v5 express bundles did exactly
 * that in four places, which is what the shared amount contract exists to stop; those files are
 * gone, so the rule is documented here rather than cited against them. The digit count comes from
 * window.fractionDigits, emitted by adyen/expressPaymentsMetadata.isml from
 * AdyenHelper.getFractionDigits().
 *
 * @param {number} major - amount in major units
 * @param {number|string} fractionDigits - digits for the currency
 * @returns {number|null} amount in minor units, or null when the input is not usable
 */
function toMinorUnits(major, fractionDigits) {
    const majorValue = typeof major === "string" ? parseFloat(major) : major;
    const digits = parseInt(fractionDigits, 10);
    if (typeof majorValue !== "number" || isNaN(majorValue) || isNaN(digits) || digits < 0) {
        return null;
    }
    return Math.round(majorValue * Math.pow(10, digits));
}

module.exports = {
    SUPPORTED_WALLETS: SUPPORTED_WALLETS,
    LOCAL_WALLETS: LOCAL_WALLETS,
    isLocalWallet: isLocalWallet,
    isLocalWalletOnContainer: isLocalWalletOnContainer,
    EXCLUDE_GOOGLE_PAY_ON_ANDROID: EXCLUDE_GOOGLE_PAY_ON_ANDROID,
    WALLET_RESPONSE_TYPES: WALLET_RESPONSE_TYPES,
    filterSupportedWallets: filterSupportedWallets,
    getWalletConfiguration: getWalletConfiguration,
    isComponentAvailable: isComponentAvailable,
    isAndroidPlatform: isAndroidPlatform,
    isWalletExcludedOnPlatform: isWalletExcludedOnPlatform,
    isProductExpressEligible: isProductExpressEligible,
    readBooleanAttribute: readBooleanAttribute,
    toMinorUnits: toMinorUnits
};
